<template>
  <div class="relative">
    <button
      type="button"
      @click="open = !open"
      class="group inline-flex items-center gap-1 max-w-full rounded-lg px-1.5 py-1 -mr-1.5 transition-all duration-150 hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-[0.97]"
      :class="open ? 'bg-gray-100 dark:bg-gray-800' : ''"
    >
      <slot name="selected" :option="selected" />
      <svg
        class="w-3.5 h-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600 transition-colors group-hover:text-gray-500 dark:group-hover:text-gray-400"
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

    <!-- Full-screen catcher so an outside click closes the menu, same
         pattern the rest of the Tasks UI uses. -->
    <div v-if="open" @click="open = false" class="fixed inset-0 z-20"></div>
    <div
      v-if="open"
      class="absolute right-0 top-full mt-1.5 min-w-[11rem] py-1 bg-white dark:bg-[#2A3037] border border-gray-100 dark:border-gray-700 rounded-xl shadow-lg ring-1 ring-black/5 z-30"
    >
      <button
        v-for="opt in options"
        :key="opt.value"
        type="button"
        :disabled="opt.disabled"
        @click="choose(opt)"
        class="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] font-medium text-left transition-colors"
        :class="opt.disabled
          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'"
      >
        <slot name="option" :option="opt" />
        <svg
          v-if="opt.value === modelValue"
          class="w-3.5 h-3.5 ml-auto flex-shrink-0 text-blue-600 dark:text-blue-400"
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
// The Status and Priority pickers on /tasks/:id were two ~40-line copies of
// the same trigger + click-catcher + menu markup. This is that markup once,
// with the per-property bit (how a selected value and an option render)
// left to `selected`/`option` slots so status can show its dot and priority
// its flag without either shape leaking in here.
import { ref, computed } from 'vue'

const props = defineProps({
  modelValue: { type: String, default: '' },
  // [{ value, label, disabled? }] — extra keys pass straight through to the
  // slots, which is how status/priority get at their own metadata.
  options: { type: Array, default: () => [] },
})
const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const selected = computed(() => props.options.find((o) => o.value === props.modelValue) || props.options[0])

function choose(option) {
  if (option.disabled) return
  emit('update:modelValue', option.value)
  open.value = false
}
</script>
